import {
  THREAD_CHANNELS,
  THREAD_PRIORITIES,
  THREAD_STATUSES,
  THREAD_TOPICS,
  type StaffMember,
  type ThreadChannel,
  type ThreadKind,
  type ThreadPriority,
  type ThreadTopic,
} from '@zuruflex/types';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@zuruflex/ui';
import { ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  activeFacets,
  INBOX_VIEWS,
  viewOwnsAssignee,
  viewOwnsStatus,
  type InboxFiltersHandle,
  type InboxView,
} from './filters.js';

/**
 * Which key names each view.
 *
 * Every one of these already exists and already says the right thing, so
 * the rail borrows them rather than growing a sixth spelling of "Snoozed"
 * that a translator would have to be asked for twice.
 */
const VIEW_LABEL: Record<InboxView, string> = {
  all: 'inbox.filter.all',
  mine: 'inbox.mineOnly',
  unassigned: 'inbox.filter.unassigned',
  snoozed: 'inbox.status.SNOOZED',
  breaching: 'inbox.sla.breached',
};

export interface FilterRailProps {
  handle: InboxFiltersHandle;
  staff: StaffMember[];
}

/**
 * Everything that decides which rows are below, in one band across the top.
 *
 * A band rather than a fourth column: at 1440 the three panes already
 * divide into a queue, a conversation and its context, and a rail beside
 * them takes its width out of the one pane nobody can read narrow. There
 * is no accordion, collapsible or resizable-pane primitive in `@zuruflex/ui`
 * to fold one away with, and inventing one here would put a layout
 * primitive in a page.
 *
 * The views are tabs because they are exclusive and are the first thing
 * anybody picks; the facets are menus because they are four questions
 * asked occasionally; and everything that is on shows again underneath as
 * a chip you can take off, because a filter you cannot see is a filter you
 * blame the inbox for.
 */
export function FilterRail({ handle, staff }: FilterRailProps) {
  const { t } = useTranslation(['admin', 'common']);
  const { filters, set, reset } = handle;

  /*
   * The search box is the one control that cannot be driven straight off
   * the URL: writing a search param per keystroke re-reads the query on
   * every letter and lands the caret where the last render put it. So it
   * is local while it is being typed and pushed on a pause.
   */
  const [draft, setDraft] = useState(filters.q);
  useEffect(() => setDraft(filters.q), [filters.q]);
  useEffect(() => {
    if (draft === filters.q) return;
    const timer = setTimeout(() => set({ q: draft }), 300);
    return () => clearTimeout(timer);
  }, [draft, filters.q, set]);

  const nameOf = (id: string) => staff.find((member) => member.id === id)?.fullName ?? id;

  const chipLabel = (chip: ReturnType<typeof activeFacets>[number]): string => {
    switch (chip.key) {
      case 'status':
        return t(`inbox.status.${chip.value}`);
      case 'topic':
        return t(`inbox.topic.${chip.value}`);
      case 'priority':
        return t(`inbox.priority.${chip.value}`);
      case 'channel':
        return t(`inbox.channel.${chip.value}`);
      case 'assignee':
        return nameOf(chip.value);
      default:
        return chip.value;
    }
  };

  const chips = activeFacets(filters);

  return (
    <div className="flex flex-col gap-3 rounded-card border border-border-default bg-surface p-3">
      <div className="flex flex-wrap items-center gap-3">
        {/*
          Renter-to-owner threads are somebody else's conversation. They
          are here because disputes arrive by telephone and an agent who
          cannot read the thread is useless on that call — and every open
          leaves an audit row saying who looked.

          A different axis from the views beside it: whose conversation
          this is, rather than where it stands. Hence its own strip.
        */}
        <Tabs
          value={filters.kind}
          onValueChange={(value) => set({ kind: value as ThreadKind, view: 'all' })}
        >
          <TabsList>
            <TabsTrigger value="SUPPORT">{t('inbox.tab.support')}</TabsTrigger>
            <TabsTrigger value="DIRECT">{t('inbox.tab.direct')}</TabsTrigger>
          </TabsList>
        </Tabs>

        {/*
          Only on the support desk. A renter↔owner thread has no assignee,
          no snooze and no target to be late against, so four of the five
          views would be empty by definition and the fifth would be "All".
        */}
        {filters.kind === 'SUPPORT' ? (
          <Tabs value={filters.view} onValueChange={(value) => set({ view: value as InboxView })}>
            <TabsList>
              {INBOX_VIEWS.map((view) => (
                <TabsTrigger key={view} value={view}>
                  {t(VIEW_LABEL[view])}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : null}

        <label className="relative ml-auto min-w-56 flex-1 sm:max-w-80">
          <span className="sr-only">{t('inbox.search')}</span>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-subtle"
            aria-hidden
          />
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('inbox.search')}
            className="pl-9"
          />
        </label>
      </div>

      {filters.kind === 'SUPPORT' ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
            {t('inbox.filters')}
          </span>

          {/*
            Status is the one facet the API takes repeated, so it is the one
            with tick boxes. `onSelect` is stopped so the menu stays open —
            picking two statuses is one decision, not two visits.
          */}
          <FacetMenu label={t('inbox.filter.status')} count={filters.status.length}>
            <DropdownMenuLabel>{t('inbox.filter.status')}</DropdownMenuLabel>
            {THREAD_STATUSES.map((status) => (
              <DropdownMenuCheckboxItem
                key={status}
                checked={filters.status.includes(status)}
                disabled={viewOwnsStatus(filters.view)}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={(checked) =>
                  set({
                    status: checked
                      ? [...filters.status, status]
                      : filters.status.filter((other) => other !== status),
                  })
                }
              >
                {t(`inbox.status.${status}`)}
              </DropdownMenuCheckboxItem>
            ))}
          </FacetMenu>

          <RadioFacet
            label={t('inbox.filter.topic')}
            value={filters.topic}
            options={THREAD_TOPICS}
            render={(topic) => t(`inbox.topic.${topic}`)}
            allLabel={t('inbox.filter.all')}
            onChange={(topic) => set({ topic: topic as ThreadTopic | null })}
          />

          <RadioFacet
            label={t('inbox.filter.priority')}
            value={filters.priority}
            options={THREAD_PRIORITIES}
            render={(priority) => t(`inbox.priority.${priority}`)}
            allLabel={t('inbox.filter.all')}
            onChange={(priority) => set({ priority: priority as ThreadPriority | null })}
          />

          <RadioFacet
            label={t('inbox.filter.channel')}
            value={filters.channel}
            options={THREAD_CHANNELS}
            render={(channel) => t(`inbox.channel.${channel}`)}
            allLabel={t('inbox.filter.all')}
            onChange={(channel) => set({ channel: channel as ThreadChannel | null })}
          />

          <FacetMenu
            label={t('inbox.filter.assignee')}
            count={filters.assignee && !viewOwnsAssignee(filters.view) ? 1 : 0}
          >
            <DropdownMenuLabel>{t('inbox.assignee')}</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={filters.assignee ?? ''}
              onValueChange={(value) => set({ assignee: value || null })}
            >
              <DropdownMenuRadioItem value="">{t('inbox.filter.all')}</DropdownMenuRadioItem>
              <DropdownMenuSeparator />
              {staff
                .filter((member) => member.isActive)
                .map((member) => (
                  <DropdownMenuRadioItem
                    key={member.id}
                    value={member.id}
                    disabled={viewOwnsAssignee(filters.view)}
                  >
                    {member.fullName}
                  </DropdownMenuRadioItem>
                ))}
            </DropdownMenuRadioGroup>
          </FacetMenu>
        </div>
      ) : null}

      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <Badge key={`${chip.key}:${chip.value}`} variant="soft" className="pr-1">
              {chipLabel(chip)}
              <button
                type="button"
                onClick={() => set(chip.clear)}
                aria-label={t('common:action.clear')}
                className="rounded-full p-0.5 transition-colors hover:bg-accent/20"
              >
                <X className="size-3" aria-hidden />
              </button>
            </Badge>
          ))}
          <Button variant="ghost" size="sm" onClick={reset}>
            {t('common:action.clear')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** One facet's trigger, with the number of choices on it when there are any. */
function FacetMenu({
  label,
  count,
  children,
}: {
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          {label}
          {count > 0 ? (
            <span className="rounded-pill bg-accent px-1.5 text-[10px] font-bold tabular-nums text-accent-text">
              {count}
            </span>
          ) : null}
          <ChevronDown className="size-3.5 opacity-60" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * A facet the API takes exactly one of.
 *
 * `GET /v1/threads` has `topic`, `priority` and `channel` as single enums,
 * not repeated ones. Drawing them as tick boxes would let an agent pick two
 * and get the rows for one, which is worse than not offering the second —
 * so they are radio items in the same menu the ticked facet uses, with an
 * "All" row that is how you get back out.
 */
function RadioFacet<T extends string>({
  label,
  value,
  options,
  render,
  allLabel,
  onChange,
}: {
  label: string;
  value: T | null;
  options: readonly T[];
  render: (option: T) => string;
  allLabel: string;
  onChange: (value: string | null) => void;
}) {
  return (
    <FacetMenu label={label} count={value ? 1 : 0}>
      <DropdownMenuLabel>{label}</DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={value ?? ''}
        onValueChange={(next) => onChange(next || null)}
      >
        <DropdownMenuRadioItem value="">{allLabel}</DropdownMenuRadioItem>
        <DropdownMenuSeparator />
        {options.map((option) => (
          <DropdownMenuRadioItem key={option} value={option}>
            {render(option)}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </FacetMenu>
  );
}
